import React, { useState, useEffect } from "react";
import { Model, Firm, OrderImage } from "../types";
import { modelService, firmService, orderService } from "../services/dataService";
import { turkishIncludes, getImageUrl } from "../utils/formatters";
import { useNavigate } from "react-router-dom";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./Models.css";

interface ModelFormData {
  firmId: string;
  modelCode: string;
  modelName: string;
  description: string;
  imageUrl: string;
}

const emptyFormData: ModelFormData = {
  firmId: "",
  modelCode: "",
  modelName: "",
  description: "",
  imageUrl: "",
};

const Models: React.FC = () => {
  const navigate = useNavigate();
  const [models, setModels] = useState<Model[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFirmId, setSelectedFirmId] = useState<string>("all");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [formData, setFormData] = useState<ModelFormData>(emptyFormData);
  const [formErrors, setFormErrors] = useState<Partial<ModelFormData>>({});
  const [saving, setSaving] = useState(false);
  const [modelOrderImages, setModelOrderImages] = useState<string[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    loadModels();
    loadFirms();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const data = await modelService.getAll();
      setModels(data);
    } catch (error) {
      console.error("Modeller yüklenemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "Modeller yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadFirms = async () => {
    try {
      const data = await firmService.getFirms();
      setFirms(data);
    } catch (error) {
      console.error("Firmalar yüklenemedi:", error);
    }
  };

  const getFirmName = (firmId?: string): string => {
    if (!firmId) return "-";
    const firm = firms.find((f) => f.firmId === firmId);
    return firm?.firmName || "-";
  };

  const getAvailableFirms = (): Firm[] => {
    const firmIdsInModels = new Set<string>();
    models.forEach((model) => {
      if (model.firmId) firmIdsInModels.add(model.firmId);
    });
    return firms.filter((firm) => firmIdsInModels.has(firm.firmId));
  };

  // Modal handlers
  const handleOpenModal = async (model?: Model) => {
    if (model) {
      setEditingModel(model);
      setFormData({
        firmId: model.firmId || "",
        modelCode: model.modelCode || "",
        modelName: model.modelName || "",
        description: model.description || "",
        imageUrl: model.imageUrl || "",
      });
      // Modele ait sipariş resimlerini yükle
      loadModelOrderImages(model.modelId);
    } else {
      setEditingModel(null);
      setFormData(emptyFormData);
      setModelOrderImages([]);
    }
    setFormErrors({});
    setShowModal(true);
  };

  const loadModelOrderImages = async (modelId: string) => {
    try {
      setLoadingImages(true);
      const allOrders = await orderService.getAll();
      const modelOrders = allOrders.filter((o) => o.modelId === modelId);
      const imageUrls: string[] = [];
      const seen = new Set<string>();
      for (const order of modelOrders) {
        if (order.images) {
          for (const img of order.images) {
            if (img.imageUrl && !seen.has(img.imageUrl)) {
              seen.add(img.imageUrl);
              imageUrls.push(img.imageUrl);
            }
          }
        }
      }
      setModelOrderImages(imageUrls);
    } catch (error) {
      console.error("Sipariş resimleri yüklenemedi:", error);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingModel(null);
    setFormData(emptyFormData);
    setFormErrors({});
    setModelOrderImages([]);
  };

  const validateForm = (): boolean => {
    const errors: Partial<ModelFormData> = {};
    if (!formData.firmId) errors.firmId = "Firma seçimi zorunludur";
    if (!formData.modelCode.trim()) errors.modelCode = "Model kodu zorunludur";
    if (!formData.modelName.trim()) errors.modelName = "Model adı zorunludur";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const modelData: any = {
        firmId: formData.firmId,
        modelCode: formData.modelCode.trim(),
        modelName: formData.modelName.trim(),
        description: formData.description.trim() || undefined,
        imageUrl: formData.imageUrl.trim() || undefined,
      };

      if (editingModel) {
        modelData.modelId = editingModel.modelId;
        await modelService.update(editingModel.modelId, modelData);
        setAlertModal({
          isOpen: true,
          message: "Model başarıyla güncellendi.",
          type: "success",
        });
      } else {
        await modelService.create(modelData);
        setAlertModal({
          isOpen: true,
          message: "Model başarıyla oluşturuldu.",
          type: "success",
        });
      }

      handleCloseModal();
      await loadModels();
    } catch (error: any) {
      const msg =
        error.response?.data || error.message || "Bilinmeyen bir hata oluştu";
      setAlertModal({
        isOpen: true,
        message: `İşlem başarısız: ${msg}`,
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (model: Model) => {
    setConfirmModal({
      isOpen: true,
      message: `"${model.modelName}" (${model.modelCode}) modelini silmek istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          await modelService.delete(model.modelId);
          setAlertModal({
            isOpen: true,
            message: "Model başarıyla silindi.",
            type: "success",
          });
          await loadModels();
        } catch (error: any) {
          setAlertModal({
            isOpen: true,
            message: "Model silinemedi: " + (error.response?.data || error.message),
            type: "error",
          });
        }
      },
    });
  };

  const filteredModels = models.filter((model) => {
    const matchesSearch =
      turkishIncludes(model.modelName, searchTerm) ||
      turkishIncludes(model.modelCode, searchTerm) ||
      turkishIncludes(getFirmName(model.firmId), searchTerm);

    const matchesFirm =
      selectedFirmId === "all" || model.firmId === selectedFirmId;

    return matchesSearch && matchesFirm;
  });

  const totalModels = filteredModels.length;

  if (loading && models.length === 0) {
    return <PageLoader message="Modeller yükleniyor..." />;
  }

  return (
    <div className="models-container">
      {/* Header */}
      <div className="models-header">
        <div className="models-header-breadcrumb">
          <span>Ana Sayfa</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#999">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
          <span className="active">Model Yönetimi</span>
        </div>

        <div className="models-header-content">
          <div className="models-header-left">
            <div className="models-header-icon">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M12 18h0" />
                <path d="M12 14h0" />
                <path d="M12 10h0" />
              </svg>
            </div>
            <div>
              <h1>Model Yönetimi</h1>
              <p>Modelleri oluşturun, düzenleyin ve yönetin</p>
            </div>
          </div>

          <div className="models-header-actions">
            <div className="models-stat-card">
              <div className="models-stat-label">Toplam Model</div>
              <div className="models-stat-value">{totalModels}</div>
            </div>
            <button
              className="models-add-button"
              onClick={() => handleOpenModal()}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Yeni Model
            </button>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="models-filter-section">
        <div className="models-filter-title">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#667eea"
            strokeWidth="2"
          >
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <span>Filtrele ve Ara</span>
        </div>

        <div className="models-filter-row">
          <div className="models-search-input-wrapper">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#666"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Model adı, kodu veya firma ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={selectedFirmId}
            onChange={(e) => setSelectedFirmId(e.target.value)}
            className="models-filter-select"
          >
            <option value="all">Tüm Firmalar</option>
            {getAvailableFirms()
              .sort((a, b) => a.firmName.localeCompare(b.firmName))
              .map((firm) => (
                <option key={firm.firmId} value={firm.firmId}>
                  {firm.firmName}
                </option>
              ))}
          </select>
        </div>

        {(searchTerm || selectedFirmId !== "all") && (
          <div className="models-active-filters">
            {searchTerm && (
              <span className="models-filter-tag">
                Arama: "{searchTerm}"
                <button onClick={() => setSearchTerm("")}>✕</button>
              </span>
            )}
            {selectedFirmId !== "all" && (
              <span className="models-filter-tag">
                Firma: {getFirmName(selectedFirmId)}
                <button onClick={() => setSelectedFirmId("all")}>✕</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Models Table */}
      <div className="models-table-container">
        <div className="models-table-header">
          <h2>Model Listesi ({filteredModels.length})</h2>
        </div>

        {filteredModels.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table className="models-table">
              <thead>
                <tr>
                  <th>Model Kodu</th>
                  <th>Model Adı</th>
                  <th>Firma</th>
                  <th>Kategori</th>
                  <th>Sezon</th>
                  <th>Fiyat</th>
                  <th style={{ textAlign: "center" }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredModels.map((model, index) => (
                  <tr key={model.modelId}>
                    <td>
                      <span className="model-code-badge">
                        {model.modelCode}
                      </span>
                    </td>
                    <td>
                      <span className="model-name">{model.modelName}</span>
                    </td>
                    <td>
                      {model.firmId ? (
                        <span className="model-firm-badge">
                          {getFirmName(model.firmId)}
                        </span>
                      ) : (
                        <span className="model-empty">—</span>
                      )}
                    </td>
                    <td>
                      {model.category ? (
                        <span className="model-category-badge">
                          {model.category}
                        </span>
                      ) : (
                        <span className="model-empty">—</span>
                      )}
                    </td>
                    <td>
                      {model.season || (
                        <span className="model-empty">—</span>
                      )}
                    </td>
                    <td>
                      {model.price ? (
                        <span className="model-price">
                          {model.price.toLocaleString("tr-TR", {
                            minimumFractionDigits: 2,
                          })}{" "}
                          {model.priceCurrency || "TRY"}
                        </span>
                      ) : model.estimatedPrice ? (
                        <span className="model-price estimated">
                          ~
                          {model.estimatedPrice.toLocaleString("tr-TR", {
                            minimumFractionDigits: 2,
                          })}{" "}
                          TRY
                        </span>
                      ) : (
                        <span className="model-empty">—</span>
                      )}
                    </td>
                    <td>
                      <div className="model-actions">
                        <button
                          className="model-action-btn model-cost-btn"
                          onClick={() =>
                            navigate(`/models/${model.modelId}/costs`)
                          }
                          title="Maliyet Detayları"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" />
                          </svg>
                        </button>
                        <button
                          className="model-action-btn model-edit-btn"
                          onClick={() => handleOpenModal(model)}
                          title="Düzenle"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button
                          className="model-action-btn model-delete-btn"
                          onClick={() => handleDelete(model)}
                          title="Sil"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="models-empty-state">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="#e0e0e0"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6z" />
            </svg>
            <h3>Model bulunamadı</h3>
            <p>
              {searchTerm || selectedFirmId !== "all"
                ? "Arama ve filtre kriterlerine uygun model bulunamadı."
                : "Henüz hiç model eklenmemiş. Yeni bir model eklemek için yukarıdaki butonu kullanın."}
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div
            className="models-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="models-modal-header">
              <h2>{editingModel ? "Model Düzenle" : "Yeni Model"}</h2>
              <button
                className="models-modal-close"
                onClick={handleCloseModal}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="models-modal-body">
                {/* Temel Bilgiler */}
                <div className="models-form-section">
                  <h3 className="models-form-section-title">Temel Bilgiler</h3>
                  <div className="models-form-row three">
                    <div className="models-form-group">
                      <label>
                        Firma <span className="required">*</span>
                      </label>
                      <select
                        value={formData.firmId}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firmId: e.target.value,
                          })
                        }
                        className={formErrors.firmId ? "error" : ""}
                      >
                        <option value="">Firma seçin</option>
                        {firms.map((firm) => (
                          <option key={firm.firmId} value={firm.firmId}>
                            {firm.firmName}
                          </option>
                        ))}
                      </select>
                      {formErrors.firmId && (
                        <span className="error-message">
                          {formErrors.firmId}
                        </span>
                      )}
                    </div>
                    <div className="models-form-group">
                      <label>
                        Model Kodu <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.modelCode}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            modelCode: e.target.value,
                          })
                        }
                        className={formErrors.modelCode ? "error" : ""}
                        placeholder="Örn: MDL-001"
                      />
                      {formErrors.modelCode && (
                        <span className="error-message">
                          {formErrors.modelCode}
                        </span>
                      )}
                    </div>
                    <div className="models-form-group">
                      <label>
                        Model Adı <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.modelName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            modelName: e.target.value,
                          })
                        }
                        className={formErrors.modelName ? "error" : ""}
                        placeholder="Model adını girin"
                      />
                      {formErrors.modelName && (
                        <span className="error-message">
                          {formErrors.modelName}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="models-form-row single">
                    <div className="models-form-group">
                      <label>Açıklama</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        placeholder="Model açıklaması girin"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                {/* Görsel */}
                <div className="models-form-section">
                  <h3 className="models-form-section-title">Görsel</h3>

                  {/* Mevcut görsel önizleme */}
                  {formData.imageUrl && (
                    <div className="models-image-preview">
                      <img
                        src={getImageUrl(formData.imageUrl)}
                        alt="Model görseli"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <button
                        type="button"
                        className="models-image-remove"
                        onClick={() =>
                          setFormData({ ...formData, imageUrl: "" })
                        }
                        title="Görseli kaldır"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* Sipariş resimlerinden seç */}
                  {modelOrderImages.length > 0 && (
                    <div className="models-order-images">
                      <label className="models-order-images-label">
                        Siparişlerdeki resimlerden seçin
                      </label>
                      <div className="models-order-images-grid">
                        {modelOrderImages.map((imgUrl, idx) => (
                          <div
                            key={idx}
                            className={`models-order-image-item${
                              formData.imageUrl === imgUrl ? " selected" : ""
                            }`}
                            onClick={() =>
                              setFormData({ ...formData, imageUrl: imgUrl })
                            }
                          >
                            <img
                              src={getImageUrl(imgUrl)}
                              alt={`Sipariş görseli ${idx + 1}`}
                            />
                            {formData.imageUrl === imgUrl && (
                              <div className="models-order-image-check">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="white"
                                >
                                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                </svg>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {loadingImages && (
                    <p className="models-loading-images">Sipariş resimleri yükleniyor...</p>
                  )}
                  {!loadingImages && modelOrderImages.length === 0 && editingModel && (
                    <p className="models-no-images">Bu modele ait siparişlerde görsel bulunamadı.</p>
                  )}
                </div>
              </div>

              <div className="models-modal-footer">
                <button
                  type="button"
                  className="models-btn-cancel"
                  onClick={handleCloseModal}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="models-btn-save"
                  disabled={saving}
                >
                  {saving
                    ? "Kaydediliyor..."
                    : editingModel
                    ? "Güncelle"
                    : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default Models;
